import React from 'react';
import { Drawer, Box, IconButton, Typography, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { dsVar } from '../theme/tokens';

export interface EntityDetailDrawerProps {
    /** Controls whether the drawer is visible */
    open: boolean;
    /** Called when the user closes the drawer (backdrop click or close button) */
    onClose: () => void;
    /** Icon shown in the header badge. Defaults to PsychologyIcon. */
    icon?: React.ReactNode;
    /** Primary heading text */
    title: string;
    /** Optional small label rendered above the title (e.g. a domain / category code) */
    subtitle?: string;
    /** Full description rendered in the scrollable body */
    description: string;
    /** Footer content — typically one or two action buttons */
    actions?: React.ReactNode;
    /** Content rendered at the top of the body, above the description (e.g. a back link) */
    banner?: React.ReactNode;
    /** Extra content rendered in the scrollable body, below the description */
    children?: React.ReactNode;
    /**
     * Dock the drawer into the page instead of overlaying it: the panel takes
     * layout width and the surrounding content shrinks, leaving the page live and
     * interactive. Requires the drawer to be a flex child of the page's row
     * container. Falls back to the overlay below `lg` (1024px), where there is not
     * enough width to shrink into.
     */
    docked?: boolean;
    /**
     * Viewport width at which `docked` starts pushing layout instead of overlaying.
     *
     * Defaults to Tailwind's `lg`, which is right for a page whose only other column is
     * the content. A page carrying a nav rail as well — the Risk Library — needs more
     * room before shrinking the table is reasonable, so it raises this.
     */
    dockedMinWidth?: number;
    /** Panel width in px. Defaults to 440; content with charts or wide controls may need more. */
    width?: number;
    /** Whether the panel is at its wide width. Read only alongside onToggleExpand. */
    expanded?: boolean;
    /**
     * Draws a widen/narrow toggle in the header. The caller owns the state, since a
     * docked drawer at its wide width leaves the page room for one narrow column:
     * the page must move any other column out of the way while it is wide.
     */
    onToggleExpand?: () => void;
}

const DRAWER_WIDTH = 440;
const DEFAULT_DOCKED_MIN_WIDTH = 1024;
// Wide enough for a network graph beside its detail panel. Docked, it leaves the page
// 25rem: a collapsed nav rail and one narrow column.
const EXPANDED_DOCKED_WIDTH = 'min(64rem, calc(100vw - 25rem))';
// Overlaid, a strip of the page stays showing so the backdrop can still be clicked away.
const EXPANDED_OVERLAY_WIDTH = 'min(64rem, calc(100vw - 4rem))';

/**
 * EntityDetailDrawer
 *
 * A reusable right-anchored detail drawer used across card-based pages
 * (OperatorsMarketplace, ProjectReports, IntelligenceHub, etc.) to show
 * the full entity description and primary actions when a card is clicked.
 *
 * Structure:
 *   Header  — icon badge + title (+ optional subtitle) + close button
 *   Body    — scrollable full description paragraph, plus any `children`
 *   Footer  — slot for caller-supplied action buttons (via `actions` prop)
 */
/** Markdown token styles for drawer body copy. Exported so other drawer sections
 *  (e.g. policy narratives) render identically to the description. */
export const descriptionMarkdownComponents = {
    p: ({ children }: { children?: React.ReactNode }) => (
        <Typography component="p" sx={{ fontSize: '0.875rem', color: dsVar('fg-soft'), lineHeight: 1.7, mb: 1.5, '&:last-child': { mb: 0 } }}>
            {children}
        </Typography>
    ),
    strong: ({ children }: { children?: React.ReactNode }) => (
        <Typography component="span" sx={{ fontWeight: 700, color: dsVar('fg-body') }}>
            {children}
        </Typography>
    ),
    em: ({ children }: { children?: React.ReactNode }) => (
        <Typography component="span" sx={{ fontStyle: 'italic' }}>
            {children}
        </Typography>
    ),
    h1: ({ children }: { children?: React.ReactNode }) => (
        <Typography component="div" sx={{ fontSize: '1.1rem', fontWeight: 700, color: dsVar('fg-body'), mb: 1, mt: 2, '&:first-of-type': { mt: 0 } }}>
            {children}
        </Typography>
    ),
    h2: ({ children }: { children?: React.ReactNode }) => (
        <Typography component="div" sx={{ fontSize: '1rem', fontWeight: 700, color: dsVar('fg-body'), mb: 1, mt: 2, '&:first-of-type': { mt: 0 } }}>
            {children}
        </Typography>
    ),
    h3: ({ children }: { children?: React.ReactNode }) => (
        <Typography component="div" sx={{ fontSize: '0.9rem', fontWeight: 600, color: dsVar('fg-body'), mb: 0.75, mt: 1.5, '&:first-of-type': { mt: 0 } }}>
            {children}
        </Typography>
    ),
    ul: ({ children }: { children?: React.ReactNode }) => (
        <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1.5, listStyleType: 'disc' }}>
            {children}
        </Box>
    ),
    ol: ({ children }: { children?: React.ReactNode }) => (
        <Box component="ol" sx={{ m: 0, pl: 2.5, mb: 1.5, listStyleType: 'decimal' }}>
            {children}
        </Box>
    ),
    li: ({ children }: { children?: React.ReactNode }) => (
        <Typography component="li" sx={{ fontSize: '0.875rem', color: dsVar('fg-soft'), lineHeight: 1.7, mb: 0.5 }}>
            {children}
        </Typography>
    ),
};

const EntityDetailDrawer: React.FC<EntityDetailDrawerProps> = ({
    open,
    onClose,
    icon,
    title,
    subtitle,
    description,
    actions,
    banner,
    children,
    docked,
    dockedMinWidth = DEFAULT_DOCKED_MIN_WIDTH,
    width = DRAWER_WIDTH,
    expanded = false,
    onToggleExpand,
}) => {
    // Tailwind's breakpoints, not MUI's — the page grid's breakpoints are Tailwind's.
    const isWideViewport = useMediaQuery(`(min-width:${dockedMinWidth}px)`);
    const pushLayout = Boolean(docked) && isWideViewport;

    // A persistent drawer has no modal behaviour of its own, so Escape is wired up
    // by hand to match what users get from the overlay variant.
    React.useEffect(() => {
        if (!pushLayout || !open) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [pushLayout, open, onClose]);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            variant={pushLayout ? 'persistent' : 'temporary'}
            sx={pushLayout ? {
                // Collapsing the docked width to 0 is what squeezes the sibling
                // content column; the paper slides within it.
                width: open ? (expanded ? EXPANDED_DOCKED_WIDTH : width) : 0,
                flexShrink: 0,
                overflowX: 'hidden',
                transition: 'width 225ms cubic-bezier(0.4, 0, 0.6, 1)',
            } : undefined}
            PaperProps={{
                sx: {
                    // Docked, the paper follows the root's animated width when widening or
                    // narrowing, but holds its own while the root opens or closes from 0, so
                    // the content does not reflow as it slides. Overlaid, the Slide transition
                    // owns the paper's inline transition, so a width change snaps.
                    ...(pushLayout
                        ? { width: '100%', minWidth: width }
                        : { width: { xs: '100vw', sm: expanded ? EXPANDED_OVERLAY_WIDTH : width } }),
                    backgroundColor: dsVar('surface'),
                    color: dsVar('fg-body'),
                    borderLeft: `1px solid ${dsVar('line')}`,
                    display: 'flex',
                    flexDirection: 'column',
                    // Docked papers must flow inside the drawer root rather than
                    // pinning themselves to the viewport.
                    ...(pushLayout && { position: 'relative' }),
                },
            }}
        >
            <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-accent/20 border border-accent/30 flex items-center justify-center text-accent-fg">
                            {icon ?? <PsychologyIcon />}
                        </div>
                        <div>
                            {subtitle && (
                                <p className="text-[10px] font-mono text-fg-muted uppercase tracking-tighter m-0">
                                    {subtitle}
                                </p>
                            )}
                            <h3 className="text-xl font-bold text-fg m-0 font-mono leading-snug">
                                {title}
                            </h3>
                        </div>
                    </div>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        {onToggleExpand && (
                            // A phone-width overlay is the full screen at either width.
                            <IconButton
                                onClick={onToggleExpand}
                                aria-label={expanded ? 'Narrow panel' : 'Widen panel'}
                                title={expanded ? 'Narrow panel' : 'Widen panel'}
                                sx={{ color: dsVar('fg-2'), display: { xs: 'none', sm: 'inline-flex' }, '&:hover': { color: dsVar('accent-fg') } }}
                            >
                                {expanded ? <CloseFullscreenIcon sx={{ fontSize: 18 }} /> : <OpenInFullIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                        )}
                        <IconButton onClick={onClose} sx={{ color: dsVar('fg-2') }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Body — scrollable description, plus any caller-supplied content */}
                <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }} className="font-sans">
                    {banner}
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={descriptionMarkdownComponents}>
                        {description}
                    </ReactMarkdown>
                    {children}
                </Box>

                {/* Footer — caller-supplied action buttons */}
                {actions && (
                    <Box sx={{ borderTop: `1px solid ${dsVar('line')}`, pt: 2, mt: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {actions}
                    </Box>
                )}

            </Box>
        </Drawer>
    );
};

export default EntityDetailDrawer;
